import { useState } from 'react';
import { api } from '../api.js';
import { t } from '../i18n.js';

type Mode = 'TADO_MODE' | 'TIMER_1' | 'TIMER_2' | 'TIMER_4' | 'MANUAL';

interface Props {
  initial: number;
  onClose: () => void;
}

const TEMP_MIN = 5;
const TEMP_MAX = 25;
const TEMP_STEP = 0.5;

const CHIPS: { key: Mode; labelKey: string }[] = [
  { key: 'TADO_MODE', labelKey: 'set_temp.until_next_change' },
  { key: 'TIMER_1', labelKey: 'set_temp.for_1h' },
  { key: 'TIMER_2', labelKey: 'set_temp.for_2h' },
  { key: 'TIMER_4', labelKey: 'set_temp.for_4h' },
  { key: 'MANUAL', labelKey: 'set_temp.manual' },
];

export function SetTempSheet({ initial, onClose }: Props) {
  const [temp, setTemp] = useState(initial);
  const [mode, setMode] = useState<Mode>('TADO_MODE');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dec = () => setTemp((t) => Math.max(TEMP_MIN, +(t - TEMP_STEP).toFixed(1)));
  const inc = () => setTemp((t) => Math.min(TEMP_MAX, +(t + TEMP_STEP).toFixed(1)));

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.setOverride(temp, modeToTermination(mode));
      onClose();
    } catch {
      setErr(t('set_temp.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t('set_temp.dialog_label')}>
        <div className="sheet-grab" />
        <div className="sheet-title">{t('set_temp.title')}</div>
        <div className="picker-row">
          <button
            className="picker-step"
            onClick={dec}
            disabled={temp <= TEMP_MIN || busy}
            aria-label={t('set_temp.decrease')}
          >−</button>
          <div className="picker-val">
            {temp.toFixed(1)}<span className="deg">°C</span>
          </div>
          <button
            className="picker-step"
            onClick={inc}
            disabled={temp >= TEMP_MAX || busy}
            aria-label={t('set_temp.increase')}
          >+</button>
        </div>
        <div className="picker-help">{t('set_temp.help', { min: TEMP_MIN, max: TEMP_MAX })}</div>
        <div className="chips">
          {CHIPS.map((c) => (
            <button
              key={c.key}
              className={`chip${mode === c.key ? ' is-active' : ''}`}
              onClick={() => setMode(c.key)}
              disabled={busy}
            >{t(c.labelKey)}</button>
          ))}
        </div>
        {err && <div style={{ color: 'var(--danger)', textAlign: 'center', marginTop: 10, fontSize: 13 }}>{err}</div>}
        <button className="sheet-cta" onClick={() => void submit()} disabled={busy}>
          {busy ? t('set_temp.cta_busy') : t('set_temp.cta')}
        </button>
      </div>
    </div>
  );
}

function modeToTermination(mode: Mode):
  | { type: 'TADO_MODE' }
  | { type: 'MANUAL' }
  | { type: 'TIMER'; seconds: number } {
  if (mode === 'TADO_MODE') return { type: 'TADO_MODE' };
  if (mode === 'MANUAL') return { type: 'MANUAL' };
  const hours = mode === 'TIMER_1' ? 1 : mode === 'TIMER_2' ? 2 : 4;
  return { type: 'TIMER', seconds: hours * 3600 };
}
